import { useParams, Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Status, Stat, Spinner, Empty, relTime, fmtNum } from '../../components/ui.jsx'

export function Overview() {
  const { slug } = useParams()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['overview', slug],
    queryFn: () => api.overview(slug),
    refetchInterval: (r) => (r.state.data?.deploy?.running ? 4000 : 25_000),
  })
  const proj = useQuery({ queryKey: ['project', slug], queryFn: () => api.project(slug) })
  const deploy = useMutation({
    mutationFn: () => api.deploy(slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overview', slug] }),
  })

  if (q.isPending) return <Spinner />
  if (q.isError) return <Empty title="No disponible">No se pudo cargar el proyecto.</Empty>

  const o = q.data
  const esAdmin = proj.data?.role === 'admin'

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1">{o.slug}</h1>
          <p className="lede" style={{ marginBottom: 0 }}>
            {o.domain
              ? <a href={`https://${o.domain}`} target="_blank" rel="noreferrer"
                   style={{ color: 'var(--accent)' }}>{o.domain} ↗</a>
              : 'sin dominio'}
          </p>
        </div>
        {esAdmin && (
          <button className="btn" onClick={() => deploy.mutate()} disabled={deploy.isPending}>
            {deploy.isPending ? 'Lanzando…' : 'Desplegar'}
          </button>
        )}
      </div>

      {deploy.isError && (
        <div className="alert error" style={{ marginBottom: 16 }}>
          No se pudo lanzar el despliegue: {deploy.error.message}
        </div>
      )}

      <div className="grid-stats" style={{ marginTop: 20 }}>
        <div className="stat">
          <div className="stat-label">Estado</div>
          <div style={{ marginTop: 4 }}><Status status={o.deploy.last?.status} /></div>
          <div className="stat-sub">{relTime(o.deploy.last?.startedAt)}</div>
        </div>
        <Stat label="Despliegues" value={fmtNum(o.deploy.total)}
              sub={`${o.deploy.ok} correctos · ${o.deploy.failed} fallidos`} />
        <Stat label="Base de datos" value={o.db?.size ?? '—'}
              sub={o.db ? `${o.db.tables} tabla${o.db.tables === 1 ? '' : 's'}` : 'no disponible'} />
        <Stat label="Conexiones" value={o.db ? fmtNum(o.db.connections) : '—'} sub="activas ahora" />
      </div>

      <div className="section">
        <div className="section-head">
          <h2 className="h2">Último despliegue</h2>
          <Link className="btn secondary sm" to={`/p/${slug}/deployments`}>Ver todos</Link>
        </div>
        {o.deploy.last ? (
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Status status={o.deploy.last.status} />
              <span className="ellipsis">{o.deploy.last.title || 'Despliegue'}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--fg-3)', fontSize: 13 }}>
                {relTime(o.deploy.last.startedAt)}
              </span>
              <Link className="btn secondary sm" to={`/p/${slug}/b/${o.deploy.last.id}`}>Ver registro</Link>
            </div>
          </div>
        ) : (
          <Empty title="Sin despliegues">Todavía no se ha desplegado nada en este proyecto.</Empty>
        )}
      </div>

      <div className="section">
        <h2 className="h2" style={{ marginBottom: 12 }}>Detalles</h2>
        <div className="card">
          <div className="table-scroll">
            <table className="t">
              <tbody>
                <tr><td style={{ color: 'var(--fg-2)', width: 200 }}>Repositorio</td>
                    <td className="mono">{o.gitlabPath ?? '—'}</td></tr>
                <tr><td style={{ color: 'var(--fg-2)' }}>Base de datos</td>
                    <td className="mono">{o.dbName ?? '—'}</td></tr>
                <tr><td style={{ color: 'var(--fg-2)' }}>Tu rol</td>
                    <td>{esAdmin ? 'Administrador' : 'Miembro'}</td></tr>
                <tr><td style={{ color: 'var(--fg-2)' }}>Estado</td>
                    <td>{o.state}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
